import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Linkedin, Facebook, Youtube, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import { FundingDisclosure } from "@/components/FundingDisclosure";
import { LangToggle } from "@/pages/Legal";
import { useAuth } from "@/context/AuthContext";
import { useLandingLang } from "@/components/FundingCaseStudy";
import { TrustAssistant } from "@/components/site/TrustAssistant";

const T = {
  en: { how: "How it works", areas: "Packages", review: "Human review", pricing: "Pricing", verified: "Verified businesses", signin: "Sign in", signinShort: "Sign in", dash: "Dashboard", openDash: "Open dashboard",
    about: "About", features: "Features", customers: "Customer stories", industries: "Grants by industry", cohort: "Alpha Founder Cohort",
    copy: "Peer Verify LLC. GrantVerify is a product of Peer Verify LLC. AI-researched listings should be confirmed with the issuing agency." },
  es: { how: "Cómo funciona", areas: "Paquetes", review: "Revisión humana", pricing: "Precios", verified: "Negocios verificados", signin: "Iniciar sesión", signinShort: "Entrar", dash: "Panel", openDash: "Abrir panel",
    about: "Quiénes somos", features: "Funciones", customers: "Historias de clientes", industries: "Subvenciones por industria", cohort: "Cohorte Alfa de Fundadores",
    copy: "Peer Verify LLC. GrantVerify es un producto de Peer Verify LLC. Confirme con la agencia emisora los programas investigados con IA." },
};

const SOCIAL = [["https://www.linkedin.com/company/grantverify", "LinkedIn", Linkedin], ["https://www.facebook.com/grantverify", "Facebook", Facebook], ["https://www.youtube.com/@GrantVerify", "YouTube", Youtube]];

export function SiteNav({ home = false, extra = null }) {
  const { user } = useAuth();
  const L = T[useLandingLang()];
  const p = home ? "" : "/";
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const main = [["/features", L.features, "features"], ["/pricing", L.pricing, "pricing"], ["/customers", L.customers, "customers"], ["/verified", L.verified, "badge-wall"], ["/about", L.about, "about"]];
  const links = [...main, [`${p}#how`, L.how, "how"], [`${p}#areas`, L.areas, "areas"], [`${p}#review`, L.review, "review"]];
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200" data-no-translate>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Brand />
        <nav className="hidden lg:flex gap-4 xl:gap-8 text-sm text-slate-600 whitespace-nowrap" data-testid="nav-desktop">
          {main.map(([to, label, id]) => <Link key={id} to={to} aria-current={pathname === to ? "page" : undefined} className={`transition-colors hover:text-slate-900 ${pathname === to ? "text-slate-900 font-semibold" : ""}`} data-testid={`nav-${id}-link`}>{label}</Link>)}
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-4 shrink-0">
          {extra}
          <LangToggle />
          <Link to={user ? "/dashboard" : "/login"} data-testid="nav-cta-button">
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-full h-9 sm:h-10 px-3 sm:px-5 text-sm">{user ? <><span className="sm:hidden">{L.dash}</span><span className="hidden sm:inline">{L.openDash}</span></> : <><span className="xl:hidden">{L.signinShort}</span><span className="hidden xl:inline">{L.signin}</span></>}</Button>
          </Link>
          <button onClick={() => setOpen((o) => !o)} className="lg:hidden p-1.5 text-slate-700 hover:text-slate-900" aria-label="Menu" aria-expanded={open} data-testid="nav-mobile-menu-button">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 grid gap-1" style={{ animation: "fadeUp 200ms ease" }} data-testid="nav-mobile-menu">
          {links.map(([to, label, id]) => (to.includes("#")
            ? <a key={id} href={to} onClick={() => setOpen(false)} className="py-2.5 text-sm text-slate-700 hover:text-slate-900" data-testid={`nav-mobile-${id}-link`}>{label}</a>
            : <Link key={id} to={to} onClick={() => setOpen(false)} className="py-2.5 text-sm text-slate-700 hover:text-slate-900" data-testid={`nav-mobile-${id}-link`}>{label}</Link>))}
        </nav>
      )}
    </header>
  );
}

export function SiteFooter() {
  const L = T[useLandingLang()];
  const links = [["/#how", L.how, "how"], ["/#areas", L.areas, "areas"], ["/#review", L.review, "review"], ["/about", L.about, "about"], ["/features", L.features, "features"], ["/customers", L.customers, "customers"], ["/pricing", L.pricing, "pricing"], ["/top-grant-industries", L.industries, "industries"], ["/compare/instrumentl", "GrantVerify vs Instrumentl", "vs-instrumentl"], ["/compare/quillify", "GrantVerify vs Quillify", "vs-quillify"], ["/founder-cohort", L.cohort, "cohort"]];
  return (
    <footer className="bg-slate-950 text-slate-400" data-no-translate>
      <TrustAssistant />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm" data-testid="footer-links">
        {links.map(([to, l, id]) => <Link key={to} to={to} className="hover:text-white" data-testid={`footer-${id}-link`}>{l}</Link>)}
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row justify-between gap-6">
        <div className="flex items-center gap-4">
          <Brand light />
          {SOCIAL.map(([href, name, Icon]) => (
            <a key={name} href={href} target="_blank" rel="noopener noreferrer" aria-label={`GrantVerify on ${name}`} className="h-9 w-9 rounded-full border border-white/15 grid place-items-center text-slate-300 hover:text-white hover:border-emerald-400 transition-colors" data-testid={`footer-${name.toLowerCase()}-link`}><Icon className="h-4 w-4" /></a>
          ))}
        </div>
        <p className="text-sm md:max-w-xl">© {new Date().getFullYear()} {L.copy}</p>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-10"><FundingDisclosure dark className="border-t border-white/10 pt-6" /></div>
    </footer>
  );
}
